import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import { PageSolutionGroupTableCreatePayloadDto, PageSolutionGroupTableDto, PageSolutionGroupTableQueryParamsDto, PageSolutionGroupTableUpdatePayloadDto } from "dto/pages/solution-group/page-solution-group-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import SolutionGroupTable from "components/pages/solution-group/app-solution-group-table";

const PageSolutionGroup = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const [isFetching, setIsFetching] = useState(false);
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [newRow, setNewRow] = useState<PageSolutionGroupTableCreatePayloadDto>();
	const [partnerData, setPartnerData] = useState<PageSolutionGroupTableDto[]>([]);
	const queryParams = useRef(getQueryParams<PageSolutionGroupTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetPartners = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;
			const payload = { size: 10, page };
			const response = await api.get.solution_group.list(payload);

			setPartnerData(response.data.data.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const onHandleAddPartner = useCallback(async (payload: PageSolutionGroupTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.solution_group.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			// if (partnerData && partnerData?.numberOfElements === partnerData.size) {
			// 	queryParams.current.page = partnerData.totalElements % partnerData.size === 0 ? partnerData.totalPages : partnerData.totalPages - 1;
			// }

			onHandleGetPartners();
		}
	}, [onHandleGetPartners]);

	//prettier-ignore
	const onUpdatePartners = useCallback(async (payload: PageSolutionGroupTableUpdatePayloadDto, page? : number) => {
		let response = null;
		
		try {
			await api.put.solution_group.update({ id: payload.id }, payload);
			
			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

			onHandleGetPartners();
		}
	}, [onHandleGetPartners]);

	const onHandlePageChange = (page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetPartners();
	};

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ title: "", status: "active" });
	};

	useEffect(() => {
		onHandleGetPartners();
	}, [onHandleGetPartners]);

	return (
		<div className="page-solution-group">
			<div className="group">
				<AppHeading title="Solution Group">
					<AppHeading.Divider />
				</AppHeading>

				<div className="group__button">
					<Button label="Add New" className="addNewBtn" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<SolutionGroupTable data={partnerData || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdatePartners={onUpdatePartners} onCreatePartner={onHandleAddPartner} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{currentPage: queryParams.current.page, totalPages: 1, totalElements: partnerData.length, numberOfElements: partnerData.length, tableSize: 10,}} />
			</div>
		</div>
	);
};

export default PageSolutionGroup;
